import React, { useState } from 'react';
import { FiCopy, FiPlus, FiShoppingCart } from 'react-icons/fi';
import { Button } from '../../../design-system';
import { ProductDetailModal } from './ProductDetailModal';
import {
  ProductCopy,
  ProductFeedback,
  ProductMedia,
  ProductSummary,
  ProductVariants,
  useProductCart,
} from './product';

interface CategoryProduct extends ProductSummary {
  link: string;
  is_same_city?: boolean;
  is_vendor_online?: boolean;
  is_best_vendor?: boolean;
}

interface Category {
  title: string;
  description: string;
  price_analysis?: string;
  usage_context?: string;
  key_differences?: string;
  products: CategoryProduct[];
}

interface CategorizedProductsProps {
  categories: Category[];
  onSendMessage: (message: string) => void;
  agent_chat_id?: number;
}

interface CategoryProductCardProps {
  product: CategoryProduct;
  onOpenDetail: (productId: number) => void;
  onShowSimilar: (productId: number, title: string) => void;
  agentChatId?: number;
}

const CategoryProductCard: React.FC<CategoryProductCardProps> = ({ product, onOpenDetail, onShowSimilar, agentChatId }) => {
  const cart = useProductCart(product.product_id, { agentChatId });
  const mediaProduct = { ...product, is_top_vendor: product.is_best_vendor };

  return (
    <article className="group min-w-0 overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white">
      <button type="button" onClick={() => onOpenDetail(product.product_id)} className="grid w-full grid-cols-[84px_minmax(0,1fr)] gap-2 p-2 text-right app-sm:grid-cols-[104px_minmax(0,1fr)] app-sm:gap-2.5 app-sm:p-2.5">
        <ProductMedia product={mediaProduct} className="h-[84px] w-[84px] rounded-ds-10 app-sm:h-[104px] app-sm:w-[104px]" />
        <div className="min-w-0">
          <ProductCopy product={product} price={cart.selectedPrice} compact />
        </div>
      </button>

      {cart.showVariants && cart.variantOptions.length > 0 ? (
        <div className="border-t border-ds-100 p-2">
          <ProductVariants options={cart.variantOptions} selectedId={cart.selectedVariantId} onSelect={cart.setSelectedVariantId} />
          <Button size="sm" fullWidth className="mt-2" onClick={() => cart.addToCart()} disabled={cart.addingToCart || !cart.selectedVariantId}>
            {cart.addingToCart ? 'در حال افزودن...' : 'تأیید و افزودن'}
          </Button>
          <ProductFeedback message={cart.message} />
        </div>
      ) : (
        <div className="flex gap-1.5 border-t border-ds-100 p-2">
          <Button size="sm" className="min-w-0 flex-1 px-2" onClick={cart.requestAddToCart} disabled={cart.loadingVariants || cart.addingToCart}>
            <FiShoppingCart size={13} />{cart.loadingVariants || cart.addingToCart ? 'یه‌کم صبر کن…' : 'افزودن به سبد'}
          </Button>
          <Button size="sm" variant="secondary" className="min-w-0 flex-1 px-2" onClick={() => onShowSimilar(product.product_id, product.title || '')}>
            <FiCopy size={13} />مشابه‌ها
          </Button>
        </div>
      )}
      {!cart.showVariants && <ProductFeedback message={cart.message} />}
    </article>
  );
};

export const CategorizedProducts: React.FC<CategorizedProductsProps> = ({ categories, onSendMessage, agent_chat_id }) => {
  const [detailProductId, setDetailProductId] = useState<number | null>(null);
  const allProducts = categories.flatMap((category) => category.products);

  const showSimilar = (productId: number, productTitle: string) => {
    onSendMessage(`محصولات مشابه «${productTitle}» (کد: ${productId}) رو ببین`);
  };

  const currentIndex = detailProductId === null ? -1 : allProducts.findIndex((product) => product.product_id === detailProductId);

  return (
    <>
      <div className="space-y-[18px]">
        {categories.map((category, categoryIndex) => (
          <section key={`${category.title}-${categoryIndex}`} className="overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white shadow-ds-1">
            <header className="bg-category-v3 border-b border-r-4 border-b-ds-100 border-r-ds-primary px-3 py-[13px] app-sm:px-4 app-sm:py-[15px]">
              <h3 className="text-base font-bold text-ds-ink">{category.title}</h3>
              {category.description && <p className="mt-1 text-[11px] leading-5 text-ds-600">{category.description}</p>}
              {(category.usage_context || category.price_analysis || category.key_differences) && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {category.usage_context && <span className="rounded-full border border-ds-100 bg-ds-canvas px-2 py-1 text-[9px] text-ds-700">مناسب برای: {category.usage_context}</span>}
                  {category.price_analysis && <span className="rounded-full border border-ds-100 bg-ds-canvas px-2 py-1 text-[9px] text-ds-700">قیمت: {category.price_analysis}</span>}
                  {category.key_differences && <span className="rounded-full border border-ds-100 bg-ds-canvas px-2 py-1 text-[9px] text-ds-700">تفاوت: {category.key_differences}</span>}
                </div>
              )}
            </header>

            <div className="grid grid-cols-1 gap-2.5 p-2.5 app-sm:p-3.5 app-lg:grid-cols-2">
              {category.products.slice(0, 6).map((product) => (
                <CategoryProductCard
                  key={product.product_id}
                  product={product}
                  onOpenDetail={setDetailProductId}
                  onShowSimilar={showSimilar}
                  agentChatId={agent_chat_id}
                />
              ))}
            </div>

            <button type="button" onClick={() => onSendMessage(`محصولات بیشتر از دسته «${category.title}» رو ببین`)} className="flex h-11 w-full items-center justify-center gap-2 border-t border-ds-100 bg-ds-primary text-xs font-bold text-white hover:bg-ds-primary-strong">
              <FiPlus />محصولات بیشتر این دسته
            </button>
          </section>
        ))}
      </div>

      {detailProductId !== null && (
        <ProductDetailModal
          productId={detailProductId}
          isOpen
          onClose={() => setDetailProductId(null)}
          agent_chat_id={agent_chat_id}
          previousProductId={currentIndex > 0 ? allProducts[currentIndex - 1].product_id : null}
          nextProductId={currentIndex >= 0 && currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1].product_id : null}
          onNavigateToProduct={setDetailProductId}
        />
      )}
    </>
  );
};
